import { cefrTagsOnly, nonCefrTags, type Direction } from "@rmnlng/shared";
import { ArrowLeft, BookMarked, CheckCheck, ChevronDown, ChevronRight, ChevronUp, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { CardImage } from "@/components/CardImage";
import { PlayAudioButton } from "@/components/PlayAudioButton";
import { Button } from "@/components/ui/button";
import { useRomanianAudio } from "@/lib/audio";
import { features } from "@/lib/features";
import { useT, type I18nKey } from "@/lib/i18n";
import { useMarkKnown } from "@/lib/reviews";
import {
  useDeleteWord,
  useWord,
  useWordRelatedGrammar,
  type WordDetail as WordDetailData,
  type WordProgressAgg,
} from "@/lib/words";
import { Chip, InflectionCard } from "./InflectionCard";

const GENDER_LABEL: Record<string, string> = {
  M: "masc.",
  F: "fem.",
  N: "neut.",
};

export function WordDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: word, isLoading, isError, error } = useWord(id);
  const del = useDeleteWord();
  const [confirming, setConfirming] = useState(false);
  const t = useT();
  // No autoplay arg — unlike the study card we don't want audio firing
  // every time the user opens a detail page. The button below triggers
  // playback on tap; the hook still handles cleanup on unmount.
  const { play } = useRomanianAudio();

  if (!id) return <Navigate to="/words" replace />;

  if (isLoading) {
    return <p className="text-[13px] text-text-3">{t("common.loading")}</p>;
  }

  if (isError || !word) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton />
        <p className="text-[13px] text-[color:var(--again)]">
          {error instanceof Error ? error.message : t("word_detail.not_found")}
        </p>
      </div>
    );
  }

  const handleDelete = async () => {
    await del.mutateAsync(word.id);
    navigate("/words", { replace: true });
  };

  const metaParts: string[] = [];
  if (word.pos) metaParts.push(word.pos.toLowerCase());
  if (word.gender && GENDER_LABEL[word.gender]) metaParts.push(GENDER_LABEL[word.gender]!);

  // Split tags into their two display slots: CEFR level sits next to the
  // lemma as a single accent chip, everything else goes into the Tags
  // section at the bottom as clickable filter chips. Same source array
  // (`word.tags`) — different presentation per kind.
  const level = cefrTagsOnly(word.tags)[0] ?? null;
  const topicalTags = nonCefrTags(word.tags);

  return (
    <div className="flex flex-col gap-4">
      {/* Sticky header — keeps the back button reachable while the long
          word detail scrolls past. `-mx-4 px-4` spans full container width
          (container has 1rem inline padding that we cancel + re-add inside).
          `bg-bg/85 backdrop-blur-md` gives an iOS-style frost so the scrolled
          content reads under it without being distracting. */}
      <div className="sticky top-0 z-10 -mx-4 bg-bg/85 px-4 py-2 backdrop-blur-md">
        <div className="-ml-2 flex items-center">
          <BackButton />
        </div>
      </div>

      {/* Hero image — same renderer used on the study card so curator
          imageUrl + on-demand Pexels gloss + "no image" placeholder
          all behave identically across the two surfaces. */}
      <CardImage
        imageUrl={word.imageUrl}
        imageQuery={word.imageQuery}
        attribution={word.imageAttribution}
        alt={word.lemma}
        placeholderText={t("word_detail.no_image")}
      />

      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.02em]">{word.lemma}</h1>
          {/* Audio playback — RO words only. Russian stubs would feed
              Romanian text into the TTS engine, which produces garbled
              pseudo-pronunciation; better to omit the affordance than
              expose it. Manual click only (no autoplay) so opening a
              detail page doesn't unexpectedly emit sound. */}
          {word.language === "RO" && <PlayAudioButton play={() => play(word.lemma)} />}
          {level && <Chip accent label={level} />}
        </div>
        {word.pronunciation && <div className="mt-1 font-mono text-[13px] text-text-3">{word.pronunciation}</div>}
        {metaParts.length > 0 && <div className="mt-1 text-[13px] text-text-3">{metaParts.join(" · ")}</div>}
      </div>

      {word.notes && <p className="-mt-2 text-[13px] italic text-text-2">{word.notes}</p>}

      {/* Translation card */}
      {word.translations.length > 0 && <TranslationCard translations={word.translations} />}

      {/* Inflection paradigm — POS-dispatched. Renders nothing when the word
          has no inflection data (PREP, CONJ, etc.). */}
      <InflectionCard word={word} />

      {/* Progress mini-card */}
      {word.progress && <ProgressCard progress={word.progress} />}

      {/* Relevant grammar — rules matching this word via structured predicate,
          explicit lemma list, or shared tags. Ordered by match strength on
          the server; we just render. */}
      <RelatedGrammarSection wordId={word.id} />

      {/* Examples */}
      {word.examples.length > 0 && (
        <section>
          <SectionLabel>{t("word_detail.examples")}</SectionLabel>
          <ul className="flex flex-col gap-2">
            {word.examples.map((e) => (
              <li key={e.id} className="rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3">
                <div className="text-[14px] text-text">{e.text}</div>
                <div className="mt-0.5 text-[12px] italic text-text-3">{e.translation}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Synonyms */}
      {word.synonyms.length > 0 && (
        <section>
          <SectionLabel>{t("word_detail.synonyms")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {word.synonyms.map((s) => (
              <Link
                key={s.id}
                to={`/words/${s.id}`}
                className="inline-flex flex-col rounded-full border border-border bg-surface px-3 py-1.5 transition-colors hover:bg-[color:var(--surface-2)]"
              >
                <span className="text-[13px] font-semibold text-text">{s.lemma}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Tags — topical only. CEFR level is excluded from this row; it's
          rendered as the accent chip next to the lemma up top. Each tag
          links to the vocab list filtered by that tag so the user can
          explore "show me every word tagged #еда" with one tap. */}
      {topicalTags.length > 0 && (
        <section>
          <SectionLabel>{t("word_detail.tags")}</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {topicalTags.map((tag) => (
              <Link
                key={tag}
                to={`/words?tag=${encodeURIComponent(tag)}`}
                className="inline-flex items-center rounded-full border border-border px-3 py-1 text-[11px] font-medium text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTAs */}
      <div className="flex gap-2 pt-2">
        {features.wordManualAdd && (
          <Button variant="secondary" className="flex-1">
            {t("word_detail.edit")}
          </Button>
        )}
        <MarkKnownAction word={word} />
      </div>

      {/* Delete — keeps the dedicated strip but far below CTAs */}
      {features.wordDelete && (
        <div className="pt-6">
          {confirming ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--r-md)] border border-[color:var(--again)]/40 bg-[color:var(--again-soft)] px-4 py-3">
              <p className="text-[13px] text-[color:var(--again)]">{t("word_detail.delete_confirm")}</p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={del.isPending}>
                  {t("common.cancel")}
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? t("word_detail.deleting") : t("word_detail.delete_yes")}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirming(true)}
              className="text-[color:var(--again)] hover:bg-[color:var(--again-soft)]"
            >
              <Trash2 />
              {t("word_detail.delete_action")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Replaces the original "Practice now" button (which just navigated to
// `/study` and didn't actually target this word). The most useful single
// action from a word's detail page is "I already know this" — graduates
// the word past the SRS queue without forcing the user through a study
// session that would only reveal the answer they already know.
//
// Hides itself once the word is mature for the user (state === "known")
// — re-confirming "I know this" on something already marked known is
// noise.
//
// Mirrors the wordManualAdd flex layout: takes flex-1 (or flex-[2] when
// the Edit button is also rendered) so the slot looks identical to the
// previous CTA shape.
//
// Direction is derived from the word's own language — couple-mature on
// the server takes care of the symmetry, so picking the natural
// direction for whichever side of the pair the user is viewing is
// enough to flip both sides to mature.
function MarkKnownAction({ word }: { word: WordDetailData }) {
  const t = useT();
  const markKnown = useMarkKnown();

  if (word.progress?.state === "known") return null;

  // Different copy for the two pre-known states. New = "I already know
  // this" framing (user discovered the word but hasn't been quizzed
  // yet). Learning = "graduate" framing (user has been studying but
  // wants to fast-forward past the remaining reviews).
  const labelKey: I18nKey =
    word.progress?.state === "learning" ? "word_detail.mark_known_learning" : "word_detail.mark_known_new";

  const direction: Direction = word.language === "RO" ? "RO_TO_RU" : "RU_TO_RO";

  function handleClick() {
    // Native confirm() to match the level-switch pattern in Settings —
    // a destructive-feeling action that benefits from a "are you sure"
    // beat without us shipping a custom modal component.
    const ok = window.confirm(t("word_detail.mark_known_confirm", { lemma: word.lemma }));
    if (!ok) return;
    markKnown.mutate({ kind: "WORD", wordId: word.id, direction });
  }

  return (
    <Button
      onClick={handleClick}
      disabled={markKnown.isPending}
      className={features.wordManualAdd ? "flex-[2]" : "flex-1"}
    >
      <CheckCheck className="size-4" />
      {markKnown.isPending ? t("common.saving") : t(labelKey)}
    </Button>
  );
}

function BackButton() {
  const t = useT();
  return (
    <Link
      to="/words"
      aria-label={t("word_detail.back_aria")}
      className="inline-flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
    >
      <ArrowLeft className="size-4" />
    </Link>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 pl-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">{children}</div>
  );
}

// Relevant grammar — rules auto-linked to this word via matcher predicate,
// explicit lemma list, or shared tags. Server ranks by match strength, so
// the first N are the strongest matches.
//
// We cap the rendered list at 3 by default to keep the section compact
// (common verbs can match 8+ rules, which would bury the rest of the page).
// A "Show more (N)" toggle at the bottom reveals the full list; tapping
// it again collapses. Matches the tap-to-expand pattern used elsewhere.
// Renders nothing when there are no matches.
const RELATED_GRAMMAR_PREVIEW_COUNT = 3;

function RelatedGrammarSection({ wordId }: { wordId: string }) {
  const t = useT();
  const { data, isPending } = useWordRelatedGrammar(wordId);
  // Collapsed by default each time the user lands on a word. Navigating to a
  // different word remounts this component (route change), so state resets
  // naturally — no need to key on wordId.
  const [expanded, setExpanded] = useState(false);
  // Don't flash a skeleton for this block — it's a secondary panel. If the
  // fetch is slow, it just appears later; better than a flicker of empty.
  if (isPending || !data || data.length === 0) return null;

  const shown = expanded ? data : data.slice(0, RELATED_GRAMMAR_PREVIEW_COUNT);
  const hiddenCount = data.length - RELATED_GRAMMAR_PREVIEW_COUNT;
  const canToggle = data.length > RELATED_GRAMMAR_PREVIEW_COUNT;

  return (
    <section>
      <div className="mb-2 pl-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("word_detail.related_grammar")}
      </div>
      <ul className="flex flex-col gap-2">
        {shown.map((rule) => (
          <li key={rule.id}>
            <Link
              to={`/grammar/${rule.id}`}
              className="flex items-center gap-3 rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3 transition-colors hover:bg-[color:var(--surface-2)]"
            >
              <div
                className="flex size-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
              >
                <BookMarked className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold overflow-hidden text-ellipsis text-nowrap">{rule.title}</div>
                {rule.tags.length > 0 && (
                  <div className="mt-0.5 truncate text-[12px] text-text-3">
                    {rule.tags.map((tag) => `#${tag}`).join(" ")}
                  </div>
                )}
              </div>
              <ChevronRight className="size-4 shrink-0 text-text-3" />
            </Link>
          </li>
        ))}
      </ul>
      {canToggle && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-[var(--r-md)] py-2 text-[12px] font-medium text-text-3 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
        >
          {expanded ? (
            <>
              <ChevronUp className="size-3.5" />
              {t("word_detail.related_grammar_show_less")}
            </>
          ) : (
            <>
              <ChevronDown className="size-3.5" />
              {t("word_detail.related_grammar_show_more", { n: hiddenCount })}
            </>
          )}
        </button>
      )}
    </section>
  );
}

function TranslationCard({ translations }: { translations: WordDetailData["translations"] }) {
  const primary = translations[0]!;
  const rest = translations.slice(1);
  const t = useT();
  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
      <SectionLabel>{t("word_detail.translation")}</SectionLabel>
      <div className="text-[22px] font-semibold tracking-[-0.01em] text-[color:var(--accent)]">{primary.lemma}</div>
      {rest.length > 0 && (
        <div className="mt-1 text-[13px] text-text-2">
          {t("word_detail.also_prefix")} {rest.map((tr) => tr.lemma).join(", ")}
        </div>
      )}
    </div>
  );
}

function ProgressCard({ progress }: { progress: WordProgressAgg }) {
  const t = useT();
  const stateLabel = progress.state === "known" ? t("common.known") : t("common.learning");
  const nextReview = formatNextReview(progress.nextReviewAt, t);
  const pct = progressPct(progress);

  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>{t("word_detail.your_progress")}</SectionLabel>
        <span className="font-mono text-[11px] text-text-3">
          EF {progress.easeFactor.toFixed(1)} · rep {progress.repetitions}
        </span>
      </div>
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-[15px] font-semibold">{stateLabel}</span>
        <span className="text-[12px] text-text-3">· {nextReview}</span>
      </div>
      <div className="progress-track">
        <div className="fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// `t` is passed in rather than looked up here — this function is called from
// within a component render and the hook is already held by the caller.
function formatNextReview(iso: string, t: ReturnType<typeof useT>): string {
  const next = new Date(iso);
  const now = new Date();
  const diffMs = next.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return t("word_detail.next_review_overdue", { n: Math.abs(diffDays) });
  if (diffDays === 0) return t("word_detail.next_review_today");
  if (diffDays < 30) return t("word_detail.next_review_days", { n: diffDays });
  const months = Math.round(diffDays / 30);
  return t("word_detail.next_review_months", { n: months });
}

function progressPct(p: WordProgressAgg): number {
  // Map repetitions to a 0–100% fill: 0→0, 1→25, 2→50, 3→75, 4+→100.
  const steps: Record<number, number> = { 0: 0, 1: 25, 2: 50, 3: 75 };
  return steps[p.repetitions] ?? Math.min(100, 75 + (p.repetitions - 3) * 5);
}
